import { useCallback } from 'react';
import { TExcludedTime } from '@timeedit/types/lib/types';
import { Button, Icon, TEInput } from '@timeedit/ui-components';
import { Space } from '@timeedit/ui-components/antd';
import TimeRangePicker from './TimeRangePicker';

type ExcludeTimesProps = {
  excludedTimes?: TExcludedTime[];
  onChange: (excludedTimes: TExcludedTime[]) => void;
  disabled?: boolean;
};
export const ExcludedTimes = ({
  excludedTimes,
  onChange,
  disabled,
}: ExcludeTimesProps) => {
  const onAdd = useCallback(() => {
    const newValue: TExcludedTime[] = [
      ...(excludedTimes || []),
      [undefined, undefined],
    ];
    onChange(newValue);
  }, [excludedTimes, onChange]);

  const onDelete = useCallback(
    (index: number) => {
      if (!excludedTimes) return;

      const newExcludedTimes = [...excludedTimes];
      newExcludedTimes.splice(index, 1);
      onChange(newExcludedTimes);
    },
    [excludedTimes, onChange]
  );

  const handleChange = useCallback(
    (index: number, excludedTime: TExcludedTime) => {
      if (!excludedTimes) return;
      const newExcludedTimes = [...excludedTimes];
      newExcludedTimes[index] = excludedTime;
      onChange(newExcludedTimes);
    },
    [excludedTimes, onChange]
  );

  return (
    <TEInput.LabelWrapper
      label="Exclude times"
      tooltip="Will exclude reserved times that overlap with the excluded times"
    >
      <Space direction="vertical" style={{ marginBottom: 3 }}>
        <Space
          direction="vertical"
          size="small"
          style={{ marginTop: excludedTimes?.length ? 6 : 0 }}
        >
          {excludedTimes?.map((excludedTime, index) => (
            <ExcludedTimeInput
              key={index}
              excludedTime={excludedTime}
              onChange={(excludedTime) => handleChange(index, excludedTime)}
              onDelete={() => onDelete(index)}
              disabled={disabled}
            />
          ))}
        </Space>
        <Button size="small" type="dashed" onClick={onAdd} disabled={disabled}>
          Add
        </Button>
      </Space>
    </TEInput.LabelWrapper>
  );
};

type ExcludedTimeInputProps = {
  excludedTime?: TExcludedTime;
  onChange: (excludedTime: TExcludedTime) => void;
  onDelete: () => void;
  disabled?: boolean;
};
const ExcludedTimeInput = ({
  excludedTime,
  onChange,
  onDelete,
  disabled,
}: ExcludedTimeInputProps) => {
  return (
    <Space>
      <TimeRangePicker
        value={{
          startTime: excludedTime?.[0],
          endTime: excludedTime?.[1],
        }}
        onChange={(val) => {
          onChange([val.startTime, val.endTime]);
        }}
        disabled={disabled}
      />
      <Button
        size="small"
        icon={<Icon id="DeleteOutlined" alt="Delete" />}
        onClick={onDelete}
        disabled={disabled}
      ></Button>
    </Space>
  );
};
